import { useAuth0 } from "@auth0/auth0-react";
import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";
import PropTypes from "prop-types";
import { Fragment, useContext, useState } from "react";
import { createUseStyles } from "react-jss";
import { useSelector } from "react-redux";
import { getAppUser } from "../../../api/entity/appuser";
import { type auth0B2BUserType, userIsB2B } from "../../../api/entity/appuser_types";
import { Button } from "../../../components/common/Button";
import { Loader } from "../../../components/widgets/Loader";
import { custom_auth, externalProfileUrl } from "../../../context";
import { useApoAuth } from "../../../hooks";
import jssVariables from "../../../jss_setup";
import { TranslationContext } from "../../../umbilical_tools";
import EditProfileModal from "./EditProfileModal";

const buttonStyles = {
  padding: `0 ${jssVariables.double_gap}`,
  minWidth: "7rem",
};
const profileStyles = createUseStyles({
  Profile: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    padding: [jssVariables.normal_gap, jssVariables.double_gap],
    background: "white",
    borderRadius: jssVariables.half_gap,
    boxShadow: jssVariables.shadows.normalShadow,
    ...(props?.customStyles?.MyProfile ?? {}),
  }),
  title: (props) => ({
    fontSize: "x-large",
    fontWeight: 500,
    ...(props?.customStyles?.title ?? {}),
  }),
  content: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    ...(props?.customStyles?.content ?? {}),
  }),
  avatar: (props) => ({
    display: "grid",
    placeItems: "center",
    background: "#d9d9d9",
    height: "3rem",
    width: "3rem",
    borderRadius: "50%",
    ...(props?.customStyles?.avatar ?? {}),
  }),
  userIcon: (props) => ({
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "50%",
    ...(props?.customStyles?.userIcon ?? {}),
  }),
  avatarIcon: (props) => ({
    fontSize: "x-large",
    color: "white",
    ...(props?.customStyles?.avatarIcon ?? {}),
  }),
  list: (props) => ({
    display: "grid",
    gridGap: `calc(${jssVariables.normal_gap} / 4)`,
    fontSize: "medium",
    ...(props?.customStyles?.list ?? {}),
  }),
  listItemLabel: (props) => ({
    fontWeight: 500,
    ...(props?.customStyles?.listItemLabel ?? {}),
  }),
  verifiedLabel: (props) => ({
    color: jssVariables.colors.base_color,
    fontWeight: 500,
    ...(props?.customStyles?.verifiedLabel ?? {}),
  }),
  actions: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2, max-content)",
    gridGap: jssVariables.normal_gap,
    ...(props?.customStyles?.actions ?? {}),
  }),
  actionBtnSpan2: (props) => ({
    gridColumn: "span 2",
    ...(props?.customStyles?.actionBtnSpan2 ?? {}),
  }),
  userDivider: {
    borderBottom: `2px solid ${jssVariables.colors.base_color}`,
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    Profile: (props) => ({
      paddingBottom: `calc(${jssVariables.normal_gap} * 4)`,
      width: "80vw",
      ...(props?.customStyles?.MyProfile?.smallscreen ?? {}),
    }),
    content: (props) => ({
      gridGap: `${jssVariables.double_gap} calc(${jssVariables.normal_gap} * 4)`,
      gridTemplateColumns: "max-content 1fr",
      alignItems: "center",
      paddingLeft: jssVariables.double_gap,
      ...(props?.customStyles?.content?.smallscreen ?? {}),
    }),
    avatar: (props) => ({
      height: "4rem",
      width: "4rem",
      ...(props?.customStyles?.avatar?.smallscreen ?? {}),
    }),
    avatarIcon: (props) => ({
      fontSize: "x-large",
      ...(props?.customStyles?.avatarIcon?.smallscreen ?? {}),
    }),
    actions: (props) => ({
      ...(props?.customStyles?.actions?.smallscreen ?? {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    Profile: (props) => ({
      width: "80vw",
      ...(props?.customStyles?.MyProfile?.bigscreen ?? {}),
    }),
  },
});
function Profile({ profile, getGuest }) {
  const classes = profileStyles();
  const [showEditModal, setShowEditModal] = useState(false);

  const allowEdit = useSelector((state) => state?.WebsiteMetadata?.metadata?.allow_profile_edit);

  const { user } = useAuth0() as { user: auth0B2BUserType | any };
  const { getIdTokenClaims } = useApoAuth();

  const isB2bUser = userIsB2B(user);

  const {
    data: appUser,
    isLoading: isAppUserLoading,
    isFetching: isAppUserFetching,
  } = useQuery({
    queryKey: ["appUser", user?.sub],
    queryFn: async () => {
      const token: { __raw: string } = await getIdTokenClaims();
      return getAppUser(user.sub, token.__raw);
    },
    enabled: !!user && isB2bUser,
    refetchOnWindowFocus: false,
  });
  const appUserData = appUser?.data;
  const isB2BUserLoading = isAppUserFetching;

  const user_picture = user?.picture;

  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  const { logout, isB2B } = useApoAuth();

  const secondary_users = profile?.ext_alt_users?.users ?? [];

  return (
    <div className={classes.Profile}>
      {showEditModal && (
        <EditProfileModal
          profile={profile}
          getGuest={getGuest}
          setShowEditModal={setShowEditModal}
        />
      )}
      <div className={classes.title}>{t("profile")}</div>
      <div className={classes.content}>
        <div className={classes.avatar}>
          {user_picture ? (
            <img src={user_picture} alt="" className={classes.userIcon} />
          ) : (
            <Icon icon="ic:baseline-person" className={classes.avatarIcon} />
          )}
        </div>
        <div className={classes.list}>
          {secondary_users.length > 0 && (
            <Fragment>
              <div>
                <span>
                  <strong>Primary User</strong>
                </span>
              </div>
              <div className={classes.userDivider} />
            </Fragment>
          )}
          {!isB2bUser && (
            <Fragment>
              <div className={classes.listItem}>
                <span className={classes.listItemLabel}>{t("firstName")}: </span>
                <span>{profile?.first_name ?? "N/A"}</span>
              </div>
              <div className={classes.listItem}>
                <span className={classes.listItemLabel}>{t("lastName")}: </span>
                <span>{profile?.last_name ?? "N/A"}</span>
              </div>
              <div className={classes.listItem}>
                <span className={classes.listItemLabel}>{t("email")}: </span>
                <span>{user?.email ?? profile?.email ?? "N/A"}</span>
              </div>
            </Fragment>
          )}
          {isB2bUser && !isB2BUserLoading && !!appUserData ? (
            <Fragment>
              <div className={classes.listItem}>
                <span className={classes.listItemLabel}>{t("firstName")}: </span>
                <span>{appUserData.first_name ?? "N/A"}</span>
              </div>
              <div className={classes.listItem}>
                <span className={classes.listItemLabel}>{t("lastName")}: </span>
                <span>{appUserData.last_name ?? "N/A"}</span>
              </div>
              <div className={classes.listItem}>
                <span className={classes.listItemLabel}>{t("email")}: </span>
                <span>{appUserData.email ?? "N/A"}</span>
              </div>
            </Fragment>
          ) : isB2bUser ? (
            <Loader />
          ) : null}
          {secondary_users.length > 0 && (
            <Fragment>
              <div>
                <span>
                  <strong>Secondary Users</strong>
                </span>
              </div>
              <div className={classes.userDivider} />
            </Fragment>
          )}
          {secondary_users.map((usr, idx: number) => (
            <Fragment key={`${idx}-${usr?.email}`}>
              <div className={classes.listItem} key={idx}>
                <span className={classes.listItemLabel}>{t("firstName")}: </span>
                <span>{usr?.first_name ?? "N/A"}</span>
              </div>
              <div className={classes.listItem} key={idx}>
                <span className={classes.listItemLabel}>{t("lastName")}: </span>
                <span>{usr?.last_name ?? "N/A"}</span>
              </div>
              <div className={classes.listItem} key={idx}>
                <span className={classes.listItemLabel}>{t("email")}: </span>
                <span>{usr?.email ?? "N/A"}</span>
              </div>
              {idx > 0 && idx + 1 === secondary_users.length && (
                <div className={classes.userDivider} />
              )}
            </Fragment>
          ))}
          <div className={classes.listItem}>
            {!custom_auth && (
              <span className={`${classes.verifiedLabel} ${classes.listItemLabel}`}>
                {(user?.email_verified ?? false)
                  ? `${t("verifiedEmail")}`
                  : `${t("unverifiedEmail")}`}
              </span>
            )}
          </div>
        </div>
        <div />
        <div className={classes.actions}>
          {externalProfileUrl && (
            <div className={classes.actionBtnSpan2}>
              <Button
                text="Manage Subscription"
                onClick={() => {
                  window.location.href = externalProfileUrl;
                }}
                customStyles={{ ButtonDefault: { ...buttonStyles } }}
              />
            </div>
          )}
          {!isB2B && allowEdit && (
            <Button
              text={t("edit")}
              onClick={() => setShowEditModal(true)}
              customStyles={{ ButtonDefault: { ...buttonStyles } }}
            />
          )}
          <Button
            text={t("logout")}
            onClick={() => {
              if (typeof logout !== "function") return;
              logout({ logoutParams: { returnTo: window.location.origin } });
            }}
            customStyles={{ ButtonDefault: { ...buttonStyles } }}
          />
        </div>
      </div>
    </div>
  );
}
Profile.propTypes = {
  profile: PropTypes.object.isRequired,
  getGuest: PropTypes.func.isRequired,
};
export default Profile;
